(() => {
  if (window.dsrcvPdpVideoControls) return;
  window.dsrcvPdpVideoControls = true;

  const selector = '[data-pdp-video]';
  const bindings = new Map();
  let frame = 0;

  function pauseOutsideView(video) {
    const rect = video.getBoundingClientRect();
    if ((rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) && !video.paused) {
      video.pause();
    }
  }

  const observer = typeof window.IntersectionObserver === 'function'
    ? new window.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
      });
    }, { threshold: 0 })
    : null;

  function prepareSource(root, video) {
    let source = video.querySelector('source');
    let url = source && source.getAttribute('data-src') || root.getAttribute('data-pdp-video-source');
    if (source && source.getAttribute('src')) return;
    if (!url) return;
    if (!video.getAttribute('poster') && !url.includes('#')) url += '#t=0.001';
    if (!source) {
      source = document.createElement('source');
      source.type = 'video/mp4';
      video.appendChild(source);
    }
    source.setAttribute('src', url);
    video.preload = 'auto';
    video.setAttribute('preload', 'auto');
    if (video.matches('[data-product-campaign-video]')) video.dataset.dsrcvCampaignLoaded = 'true';
    else video.dataset.primed = 'true';
    video.load();
  }

  function bind(root) {
    if (bindings.has(root)) return;
    const video = root.querySelector('video');
    const button = root.querySelector('[data-pdp-video-control]');
    if (!video || !button) return;
    const status = root.querySelector('[data-pdp-video-status]');
    let failed = Boolean(video.error);
    let buffering = false;
    let playRequest = 0;

    function hasSource() {
      const source = video.querySelector('source');
      return Boolean(video.currentSrc || video.getAttribute('src') || source && source.getAttribute('src'));
    }

    function align() {
      const outer = root.getBoundingClientRect();
      const media = video.getBoundingClientRect();
      if (!outer.width || !media.width) return;
      button.style.right = `${Math.max(8, outer.right - media.right + 8)}px`;
      button.style.bottom = `${Math.max(8, outer.bottom - media.bottom + 8)}px`;
    }

    function update() {
      if (!video.paused && video.dataset.pdpVideoPaused === 'true') {
        video.pause();
        return;
      }
      const loading = !failed && video.dataset.pdpVideoPaused !== 'true' &&
        (hasSource() && video.readyState < 2 || buffering && !video.paused && video.readyState < 3);
      button.dataset.playing = String(!video.paused);
      button.dataset.state = failed ? 'error' : loading ? 'loading' : video.paused ? 'paused' : 'playing';
      button.setAttribute('aria-label', failed ? 'Retry video' : video.paused ? 'Play video' : 'Pause video');
      root.setAttribute('aria-busy', String(loading));
      const message = failed ? 'Video unavailable. Retry video.' : loading ? 'Loading video' : '';
      if (status && status.textContent !== message) status.textContent = message;
    }

    function markLoaded() {
      if (root.hasAttribute('data-bottom-video-wrapper')) root.classList.add('is-video-loaded');
      align();
      ready();
    }

    function ready() {
      buffering = false;
      update();
    }

    function waiting() {
      buffering = true;
      update();
    }

    function loading() {
      failed = false;
      waiting();
    }

    function error() {
      failed = true;
      buffering = false;
      update();
    }

    function playFailed(reason) {
      if (reason && (reason.name === 'AbortError' || reason.name === 'NotAllowedError')) ready();
      else error();
    }

    function toggle(event) {
      event.stopPropagation();
      if (!failed && !video.paused) {
        playRequest++;
        video.dataset.pdpVideoPaused = 'true';
        video.pause();
      } else {
        video.dataset.pdpVideoPaused = 'false';
        const retry = failed;
        const request = ++playRequest;
        failed = false;
        buffering = false;
        try {
          if (retry && hasSource()) video.load();
          else prepareSource(root, video);
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(reason => {
            if (request === playRequest) playFailed(reason);
          });
        } catch (reason) {
          playFailed(reason);
        }
      }
      update();
    }

    button.addEventListener('click', toggle);
    const events = { play: update, pause: ready, ended: ready,
      loadstart: loading, waiting, stalled: waiting, playing: ready, canplay: ready };
    Object.entries(events).forEach(([event, listener]) => video.addEventListener(event, listener));
    // A failed nested source dispatches a non-bubbling error without setting video.error.
    video.addEventListener('error', error, true);
    video.addEventListener('loadeddata', markLoaded);
    if (video.readyState >= 2) markLoaded();
    update();
    align();
    button.hidden = false;
    if (observer) observer.observe(video);
    const resize = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(align) : null;
    if (resize) {
      resize.observe(root);
      resize.observe(video);
    }
    bindings.set(root, () => {
      playRequest++;
      if (observer) observer.unobserve(video);
      if (resize) resize.disconnect();
      button.removeEventListener('click', toggle);
      Object.entries(events).forEach(([event, listener]) => video.removeEventListener(event, listener));
      video.removeEventListener('error', error, true);
      video.removeEventListener('loadeddata', markLoaded);
      if (!video.paused) video.pause();
    });
    bindings.get(root).align = align;
  }

  function scan(node) {
    if (node.matches && node.matches(selector)) bind(node);
    if (node.querySelectorAll) node.querySelectorAll(selector).forEach(bind);
  }

  function start() {
    scan(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(scan));
      bindings.forEach((cleanup, root) => {
        if (!root.isConnected) {
          cleanup();
          bindings.delete(root);
        }
      });
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', () => bindings.forEach(cleanup => cleanup.align()), { passive: true });

    if (!observer) {
      const check = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          bindings.forEach((cleanup, root) => pauseOutsideView(root.querySelector('video')));
        });
      };
      window.addEventListener('scroll', check, { passive: true, capture: true });
      window.addEventListener('resize', check, { passive: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
